import { Component, OnInit, ViewChild } from '@angular/core';
import { MIATableModel, ApiPagination } from '@mobileia/core';
import { ProductService } from 'src/app/services/product.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Product } from 'src/app/entities/product';
import { CategoryService } from 'src/app/services/category.service';
import { Category } from 'src/app/entities/category';
import { MetatagService } from 'src/app/services/metatag.service';
import { switchMap } from 'rxjs/operators';
import { SectionService } from 'src/app/services/section.service';
import { PriceRange, PriceBounds, clampPriceRange } from 'src/app/entities/price-range';
import { FiltersComponent } from './filters/filters.component';
import { BrandFilter } from 'src/app/entities/brand';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit {

  matPageIndex = 0;
  tableModel = new MIATableModel();
  dataItems = new ApiPagination<Product>();
  
  // ===== Price Filter State =====
  priceRange: PriceRange = { min: null, max: null }; // Legacy - kept for backward compatibility
  priceBounds: PriceBounds | null = null;
  
  // Catalog-wide bounds (from all products, no brand filter)
  catalogMinPrice: number = 0;
  catalogMaxPrice: number = 0;
  
  // Currently applied price range
  priceRangeApplied: [number, number] = [0, 0];
  isPriceFilterActive: boolean = false;
  
  // ===== Brand Filter State =====
  brands: BrandFilter[] = [];
  allBrands: BrandFilter[] = [];  // Store original full list with catalog price range
  selectedBrandIds: number[] = [];
  
  // ===== Other State =====
  category: Category;
  searchText = '';
  orderType = 0;
  bannerData: any;
  pageSize = 10;
  currentPage = 0;
  totalSize = 0;

  loading: boolean = false;

  @ViewChild(FiltersComponent) filtersComponent: FiltersComponent | undefined;

  constructor(
    protected productService: ProductService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected categoryService: CategoryService,
    protected metatagService: MetatagService,
    protected sectionService: SectionService,
  ) { }

  ngOnInit() {
    this.metatagService.configSlug('search');
    this.loadParams();
    this.loadBanner();
  }

  loadSearch() {
    // Orden
    if (this.orderType == 2) {
      this.tableModel.ordType.title = 'product.title';
      this.tableModel.ordType.asc = 0;
    } else if (this.orderType == 1) {
      this.tableModel.ordType.title = 'views';
      this.tableModel.ordType.asc = 1;
    } else {
      this.tableModel.ordType.title = 'product_child.price';
      this.tableModel.ordType.asc = 0;
    }

    // Realizar busqueda
    this.productService.fetchList(this.tableModel).toPromise().then(data => {
      if (!data.success) {
        return;
      }
      this.dataItems = data.response;

      // Extract price_range from the response
      if (data.response && (data.response as any).price_range) {
        const apiPriceRange = (data.response as any).price_range;
        
        // Store catalog bounds on first load or when no brand filter
        if (this.selectedBrandIds.length === 0) {
          this.catalogMinPrice = apiPriceRange.min;
          this.catalogMaxPrice = apiPriceRange.max;
        }
        
        // Update the price range for legacy compatibility
        if (!this.isPriceFilterActive) {
          this.priceRange = apiPriceRange;
        }
        
        // Calculate and update price bounds
        this.updatePriceBounds(apiPriceRange);
      }

      // Handle brands list
      // Backend returns ALL brands with counts based on search + category + stock + price
      // (brand filter is applied AFTER counting, so we see all available options)
      if (data.response && (data.response as any).brands) {
        const apiBrands: BrandFilter[] = (data.response as any).brands;
        this.brands = apiBrands;
        
        // Store full list on first load (for reference)
        if (this.allBrands.length === 0) {
          this.allBrands = [...apiBrands];
        }
      }
    });
  }

  /**
   * Update price bounds based on current brand selection.
   * Brand bounds are used for slider limits and input validation.
   */
  updatePriceBounds(apiPriceRange: PriceRange) {
    const brandMinPrice = apiPriceRange.min ?? this.catalogMinPrice;
    const brandMaxPrice = apiPriceRange.max ?? this.catalogMaxPrice;
    
    this.priceBounds = {
      catalogMinPrice: this.catalogMinPrice,
      catalogMaxPrice: this.catalogMaxPrice,
      brandMinPrice: brandMinPrice,
      brandMaxPrice: brandMaxPrice
    };
    
    // Initialize applied range if not set
    if (this.priceRangeApplied[0] === 0 && this.priceRangeApplied[1] === 0) {
      this.priceRangeApplied = [brandMinPrice, brandMaxPrice];
    }
  }

  applyFilterStock(data) {
    if (data) {
      this.tableModel.addWhere('product_child.stock', 1);
    } else {
      this.tableModel.addWhere('product_child.stock', 0);
    }
    this.matPageIndex = 0;
    this.tableModel.pageCurrent = 1;
    this.loadSearch();
  }

  /**
   * Apply price filter
   * Handles both applying and removing price filters.
   */
  applyFilterPrice(data: { from: number; to: number }) {
    if (data.from === 0 && data.to === 0) {
      // Reset price filter only - need to rebuild tableModel to clear between filters
      this.isPriceFilterActive = false;

      // Store ALL current state
      const currentSearch = this.tableModel.search;
      const currentOrderType = { ...this.tableModel.ordType };

      // Get all where clauses - wheres is an object with keys as filter names
      const wheresObject: any = {};
      for (let key in this.tableModel.wheres) {
        if (this.tableModel.wheres.hasOwnProperty(key)) {
          wheresObject[key] = this.tableModel.wheres[key];
        }
      }

      // Create fresh tableModel
      this.tableModel = new MIATableModel();
      this.tableModel.itemPerPage = 100;
      this.tableModel.search = currentSearch;
      this.tableModel.ordType = currentOrderType;

      // Re-add ALL non-price filters
      for (let key in wheresObject) {
        // Skip price-related filters
        if (!key.includes('price')) {
          const value = wheresObject[key];
          this.tableModel.addWhere(key, value);
        }
      }
      
      // Reset applied range to brand bounds
      if (this.priceBounds) {
        this.priceRangeApplied = [this.priceBounds.brandMinPrice, this.priceBounds.brandMaxPrice];
      }
    } else {
      // Apply new price filter
      this.isPriceFilterActive = true;
      this.priceRangeApplied = [data.from, data.to];

      // Remove any existing price filter first
      this.tableModel.removeWhere('product_child.price');

      // Add the new between filter
      this.tableModel.addwhereBetween('product_child.price', data.from, data.to);
    }

    this.matPageIndex = 0;
    this.tableModel.pageCurrent = 1;

    this.loadSearch();
  }

  /**
   * Apply brand filter
   * When brands change, recalculate bounds and clamp price range.
   */
  applyFilterBrand(brandIds: number[]) {
    // Reset brand filter - rebuild tableModel to ensure clean removal
    const currentSearch = this.tableModel.search;
    const currentOrderType = { ...this.tableModel.ordType };

    // Get all where clauses
    const wheresObject: any = {};
    for (let key in this.tableModel.wheres) {
      if (this.tableModel.wheres.hasOwnProperty(key)) {
        wheresObject[key] = this.tableModel.wheres[key];
      }
    }

    // Create fresh tableModel
    this.tableModel = new MIATableModel();
    this.tableModel.itemPerPage = 100;
    this.tableModel.search = currentSearch;
    this.tableModel.ordType = currentOrderType;

    // Re-add ALL non-brand filters
    for (let key in wheresObject) {
      if (!key.includes('brand_id')) {
        const value = wheresObject[key];
        this.tableModel.addWhere(key, value);
      }
    }

    // Apply new brand filter if any brands selected
    if (brandIds && brandIds.length > 0) {
      // Send as comma-separated list: product.brand_id:1,2,3
      this.tableModel.addWhere('product.brand_id', brandIds.join(','));
      this.selectedBrandIds = brandIds;
    } else {
      // Deselecting all brands = "all brands" (no brand filter)
      this.selectedBrandIds = [];
    }

    this.matPageIndex = 0;
    this.tableModel.pageCurrent = 1;
    
    this.loadSearch();
  }

  cleanFilters() {
    // Rebuild tableModel to ensure clean removal of all filters
    const currentSearch = this.tableModel.search;
    const currentOrderType = { ...this.tableModel.ordType };
    
    // Get all where clauses
    const wheresObject: any = {};
    for (let key in this.tableModel.wheres) {
      if (this.tableModel.wheres.hasOwnProperty(key)) {
        wheresObject[key] = this.tableModel.wheres[key];
      }
    }
    
    // Create fresh tableModel
    this.tableModel = new MIATableModel();
    this.tableModel.itemPerPage = 100;
    this.tableModel.search = currentSearch;
    this.tableModel.ordType = currentOrderType;
    
    // Re-add only non-price and non-brand filters
    for (let key in wheresObject) {
      if (!key.includes('price') && !key.includes('brand_id')) {
        const value = wheresObject[key];
        this.tableModel.addWhere(key, value);
      }
    }
    
    // Reset all filter state
    this.selectedBrandIds = [];
    this.isPriceFilterActive = false;
    this.priceRangeApplied = [0, 0]; // Reset to trigger re-initialization in updatePriceBounds
    
    // Load search first to get fresh bounds from API
    this.loadSearch();
  }

  loadParams() {
    // Limpiar parametros
    this.tableModel.resetWhere();
    this.tableModel.itemPerPage = 100;
    // Buscar en la URL
    this.route.queryParams.pipe(switchMap(params => {
      // Titulo del producto: use generic search string instead of a where-like clause
      if(params.search){
        this.tableModel.search = params.search;
      } else {
        this.tableModel.search = '';
      }

      // Brand filter from URL
      if(params.brand_id){
        const brandId = parseInt(params.brand_id, 10);
        this.tableModel.addWhere('product.brand_id', brandId);
        this.selectedBrandIds = [brandId];
      } else {
        this.selectedBrandIds = [];
      }

      return this.route.params;
    }))
    .subscribe(params => {
      // Categoria
      if(params.category_id){
        this.tableModel.addWhere('product.category_id', params.category_id);
        this.loadCategory(params.category_id);
      }

      // Buscar
      this.loadSearch();
    });
  }

  loadCategory(categoryId: number) {
    this.categoryService.fetchById(categoryId).toPromise().then(data => {
      this.category = data;
    });
  }

  loadBanner() {
    this.sectionService.fetchBannerSearch().toPromise().then(data => {
      if (data.success) {
        this.bannerData = data.response.data;
      }
    });
  }

  clickBanner() {
    window.open(this.bannerData.button_url);
  }

  getTitleSearch() {
    if (this.category !== undefined) {
      return this.category.title;
    }

    return '';
  }

  guardar(){
    this.loading = true;

    setTimeout(()=>{
      this.loading = false;
    },1500)
  }



  onPageChange(event: any) {
    this.tableModel.itemPerPage = event.pageSize;
    this.tableModel.pageCurrent = event.pageIndex + 1;
    this.matPageIndex = event.pageIndex + 1;
    this.loadSearch();
    scrollTo(0,0);
  }
}
